import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TextInput } from "flowbite-react";
import { Spinner } from "../atoms/Spinner";
import { HiOutlineDocumentText, HiPlus, HiOutlineTrash } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import HttpService from "../../services/http";
import { CustomButton } from "../atoms/CustomButton";
import { CharacterCounter } from "../atoms/CharacterCounter";
import AiDocTemplateExample from "./AiDocTemplateExample";
import CollapsibleHowItWorks from "../common/CollapsibleHowItWorks";
import ConfirmModal from "../public/ConfirmModal";

type TemplateField = { name: string; description: string };
type UiField = TemplateField & { _key: string };
type DocTemplate = {
  id: string;
  name: string;
  fields: UiField[];
  isNew?: boolean;
};

const withKey = (f: TemplateField): UiField => ({ ...f, _key: uuidv4() });

const LIMITS = {
  TEMPLATE_NAME: 60,
  FIELD_NAME: 60,
  FIELD_DESCRIPTION: 300,
  MAX_TEMPLATES: 40,
  MAX_FIELDS_PER_TEMPLATE: 50,
};

const AiDocumentTemplates: React.FC<{ base: string }> = ({ base }) => {
  const [t] = useTranslation();
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [templateToDelete, setTemplateToDelete] = useState<DocTemplate | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await HttpService.get<{
        element: {
          templates: { id: string; name: string; fields: TemplateField[] }[];
        };
      }>(`${base}/ai-doc-templates`);
      const loaded = (res.data?.element?.templates ?? []).map((tpl) => ({
        ...tpl,
        fields: (tpl.fields ?? []).map(withKey),
      }));
      setTemplates(loaded);
      setDirtyIds(new Set());
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  const markDirty = (id: string) =>
    setDirtyIds((prev) => new Set(prev).add(id));
  const clearDirty = (id: string) =>
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const setSaving = (id: string, on: boolean) =>
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const addTemplate = () => {
    const id = `tpl_${uuidv4()}`;
    setTemplates((prev) => [
      ...prev,
      { id, name: "", fields: [], isNew: true },
    ]);
    markDirty(id);
  };
  const setTemplateName = (id: string, name: string) => {
    setTemplates((prev) =>
      prev.map((tpl) => (tpl.id === id ? { ...tpl, name } : tpl))
    );
    markDirty(id);
  };
  const addField = (id: string) => {
    setTemplates((prev) =>
      prev.map((tpl) =>
        tpl.id === id
          ? {
              ...tpl,
              fields: [...tpl.fields, withKey({ name: "", description: "" })],
            }
          : tpl
      )
    );
    markDirty(id);
  };
  const removeField = (id: string, fi: number) => {
    setTemplates((prev) =>
      prev.map((tpl) =>
        tpl.id === id
          ? { ...tpl, fields: tpl.fields.filter((_, j) => j !== fi) }
          : tpl
      )
    );
    markDirty(id);
  };
  const setField = (
    id: string,
    fi: number,
    key: "name" | "description",
    value: string
  ) => {
    setTemplates((prev) =>
      prev.map((tpl) =>
        tpl.id === id
          ? {
              ...tpl,
              fields: tpl.fields.map((f, j) =>
                j === fi
                  ? {
                      ...f,
                      [key]:
                        key === "name" ? value.replace(/\s+/g, "_") : value,
                    }
                  : f
              ),
            }
          : tpl
      )
    );
    markDirty(id);
  };

  const saveOne = async (tpl: DocTemplate) => {
    if (!tpl.name.trim()) {
      toast.error(t("dashboard.docTemplates.nameRequired") as string);
      return;
    }

    const seen = new Set<string>();
    const fields: TemplateField[] = [];
    let droppedDuplicate = false;
    for (const f of tpl.fields) {
      const name = f.name.trim().replace(/\s+/g, "_");
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) {
        droppedDuplicate = true;
        continue;
      }
      seen.add(key);
      fields.push({ name, description: f.description.trim() });
    }
    if (droppedDuplicate) {
      toast.info(t("dashboard.docTemplates.duplicateFieldsMerged") as string);
    }

    setSaving(tpl.id, true);
    try {
      const res = await HttpService.put<{
        element: { id: string; name: string; fields: TemplateField[] };
      }>(`${base}/ai-doc-templates`, { name: tpl.name, fields });
      const saved = res.data?.element;
      setTemplates((prev) =>
        prev.map((x) =>
          x.id === tpl.id
            ? {
                id: saved?.id ?? tpl.id,
                name: saved?.name ?? tpl.name,
                fields: (saved?.fields ?? fields).map(withKey),
              }
            : x
        )
      );
      clearDirty(tpl.id);
      toast.success(t("dashboard.docTemplates.savedOne") as string);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          (t("dashboard.docTemplates.saveError") as string)
      );
    } finally {
      setSaving(tpl.id, false);
    }
  };

  const deleteOne = async (tpl: DocTemplate) => {
    if (tpl.isNew) {
      setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));
      clearDirty(tpl.id);
      return;
    }
    setSaving(tpl.id, true);
    try {
      await HttpService.delete(
        `${base}/ai-doc-templates/${encodeURIComponent(tpl.id)}`
      );
      setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));
      clearDirty(tpl.id);
      toast.success(t("dashboard.docTemplates.deletedOne") as string);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          (t("dashboard.docTemplates.deleteError") as string)
      );
    } finally {
      setSaving(tpl.id, false);
    }
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    await deleteOne(templateToDelete);
    setTemplateToDelete(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <HiOutlineDocumentText className="text-blue-600" size={20} />
        <span className="text-sm font-semibold text-gray-800">
          {t("dashboard.docTemplates.title")}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <CollapsibleHowItWorks
            collapsible
            defaultOpen={templates.length === 0}
            title={t("dashboard.docTemplates.example.toggle")}
            icon={<HiOutlineDocumentText size={18} className="text-gray-500" />}
          >
            <AiDocTemplateExample />
          </CollapsibleHowItWorks>

          {templates.map((tpl) => {
            const isDirty = dirtyIds.has(tpl.id);
            const isSaving = savingIds.has(tpl.id);
            const title =
              tpl.name.trim() ||
              (t("dashboard.docTemplates.untitled") as string);
            return (
              <CollapsibleHowItWorks
                key={tpl.id}
                collapsible
                defaultOpen={tpl.isNew}
                title={title}
                icon={
                  <HiOutlineDocumentText size={18} className="text-blue-600" />
                }
              >
                <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <label className="text-sm font-medium text-gray-700 sm:shrink-0">
                    {t("dashboard.docTemplates.nameLabel")}
                  </label>
                  <div className="w-full max-w-56">
                    <TextInput
                      sizing="sm"
                      value={tpl.name}
                      onChange={(e) => setTemplateName(tpl.id, e.target.value)}
                      placeholder={
                        t("dashboard.docTemplates.namePlaceholder") as string
                      }
                      maxLength={LIMITS.TEMPLATE_NAME}
                    />
                    {tpl.name.length > LIMITS.TEMPLATE_NAME * 0.8 && (
                      <CharacterCounter
                        currentLength={tpl.name.length}
                        maxLength={LIMITS.TEMPLATE_NAME}
                        className="mt-0.5"
                      />
                    )}
                  </div>
                </div>

                <div className="rounded-md bg-gray-50/60 border border-gray-100 p-2 sm:p-3 flex flex-col gap-3">
                  {tpl.fields.map((f, fi) => (
                    <div key={f._key} className="flex items-start gap-2">
                      <div className="flex flex-col sm:flex-row gap-2 flex-1 min-w-0">
                        <div className="w-full sm:w-56 sm:flex-none">
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            {t("dashboard.docTemplates.fieldNameLabel")}
                          </label>
                          <TextInput
                            type="text"
                            sizing="sm"
                            value={f.name}
                            onChange={(e) =>
                              setField(tpl.id, fi, "name", e.target.value)
                            }
                            placeholder={
                              t("dashboard.docTemplates.fieldName") as string
                            }
                            maxLength={LIMITS.FIELD_NAME}
                            className="w-full"
                          />
                          <div className="mt-0.5">
                            <span className="block text-xs text-gray-400">
                              {t("dashboard.docTemplates.fieldNameHint")}
                            </span>
                            {f.name.length > LIMITS.FIELD_NAME * 0.8 && (
                              <CharacterCounter
                                currentLength={f.name.length}
                                maxLength={LIMITS.FIELD_NAME}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            {t("dashboard.docTemplates.fieldDescriptionLabel")}
                          </label>
                          <TextInput
                            type="text"
                            sizing="sm"
                            value={f.description}
                            onChange={(e) =>
                              setField(
                                tpl.id,
                                fi,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder={
                              t(
                                "dashboard.docTemplates.fieldDescription"
                              ) as string
                            }
                            maxLength={LIMITS.FIELD_DESCRIPTION}
                            className="w-full min-w-0"
                          />
                          {f.description.length >
                            LIMITS.FIELD_DESCRIPTION * 0.8 && (
                            <CharacterCounter
                              currentLength={f.description.length}
                              maxLength={LIMITS.FIELD_DESCRIPTION}
                              className="mt-0.5"
                            />
                          )}
                        </div>
                      </div>
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => removeField(tpl.id, fi)}
                        className="shrink-0 mt-6 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                        tooltip={
                          t("dashboard.docTemplates.removeField") as string
                        }
                        icon={<HiOutlineTrash size={18} />}
                      />
                    </div>
                  ))}

                  {tpl.fields.length >= LIMITS.MAX_FIELDS_PER_TEMPLATE ? (
                    <span className="text-xs text-gray-400">
                      {t("dashboard.docTemplates.maxFieldsReached", {
                        count: LIMITS.MAX_FIELDS_PER_TEMPLATE,
                      })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => addField(tpl.id)}
                      className="self-start inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                    >
                      <HiPlus size={14} />{" "}
                      {t("dashboard.docTemplates.addField")}
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
                  <CustomButton
                    type="button"
                    variant="none"
                    size="icon"
                    onClick={() => setTemplateToDelete(tpl)}
                    disabled={isSaving}
                    aria-label={t("common.remove") as string}
                    className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                    icon={<HiOutlineTrash size={18} />}
                  />
                  <CustomButton
                    variant="primary"
                    icon="save"
                    title={t("common.saveShort")}
                    onClick={() => saveOne(tpl)}
                    disabled={isSaving || !isDirty}
                    isLoading={isSaving}
                    className="shrink-0"
                  />
                </div>
              </CollapsibleHowItWorks>
            );
          })}

          {templates.length >= LIMITS.MAX_TEMPLATES ? (
            <span className="self-start text-xs text-gray-400">
              {t("dashboard.docTemplates.maxTemplatesReached", {
                count: LIMITS.MAX_TEMPLATES,
              })}
            </span>
          ) : (
            <CustomButton
              variant="outline"
              icon="plus"
              title={t("dashboard.docTemplates.addTemplate")}
              onClick={addTemplate}
              className="self-start"
            />
          )}
        </div>
      )}

      <ConfirmModal
        show={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        title={t("dashboard.docTemplates.deleteTitle") as string}
        description={
          t("dashboard.docTemplates.deleteConfirm", {
            name: templateToDelete?.name || "",
          }) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setTemplateToDelete(null)}
        confirmBtnName={t("common.delete") as string}
        confirmBtnFn={confirmDelete}
        isLoading={!!templateToDelete && savingIds.has(templateToDelete.id)}
        ConfirmBtnColor="red"
      />
    </div>
  );
};

export default AiDocumentTemplates;
